import { ModernizedAuditStatus, type ExternalCourse } from "@axio/graphql/generated/graphql";
import type { CourseModernizerCourseRow } from "../../../components/adminDashboard/CourseModernizer/CourseModernizerCoursesTable";

const REVIEWABLE_MODERNIZATION_STATUSES = new Set<ModernizedAuditStatus>([
  ModernizedAuditStatus.InProgress,
  ModernizedAuditStatus.Modernized,
]);

const ACTIVE_MODERNIZATION_STATUSES = new Set<ModernizedAuditStatus>([
  ModernizedAuditStatus.NotStarted,
  ModernizedAuditStatus.InProgress,
  ModernizedAuditStatus.Modernized,
]);

export const formatModernizedScore = (totalScore?: number | null, totalMaxScore?: number | null): string => {
  if (typeof totalScore !== "number") {
    return "—";
  }

  if (typeof totalMaxScore === "number" && totalMaxScore > 0) {
    return `${totalScore}/${totalMaxScore}`;
  }

  return `${totalScore}`;
};

const getModernizedCourseRank = (status: ModernizedAuditStatus | null | undefined): number => {
  if (!status) {
    return -1;
  }

  switch (status) {
    case ModernizedAuditStatus.InProgress:
      return 3;
    case ModernizedAuditStatus.Modernized:
      return 2;
    case ModernizedAuditStatus.NotStarted:
      return 1;
    default:
      return -1;
  }
};

const getModernizedCourseTimestamp = (created?: string | null, lastModified?: string | null): number => {
  const timestamp = lastModified ?? created;
  return timestamp ? new Date(timestamp).getTime() : 0;
};

export const getActiveModernizedCourse = (course: ExternalCourse) => {
  const candidates = (course.modernizedCourses ?? [])
    .filter((modernizedCourse): modernizedCourse is NonNullable<typeof modernizedCourse> => Boolean(modernizedCourse))
    .filter(
      (modernizedCourse) =>
        Boolean(modernizedCourse.status) &&
        ACTIVE_MODERNIZATION_STATUSES.has(modernizedCourse.status as ModernizedAuditStatus)
    )
    .sort((left, right) => {
      const rankDifference = getModernizedCourseRank(right.status) - getModernizedCourseRank(left.status);
      if (rankDifference !== 0) {
        return rankDifference;
      }

      return (
        getModernizedCourseTimestamp(right.created ?? null, right.lastModified ?? null) -
        getModernizedCourseTimestamp(left.created ?? null, left.lastModified ?? null)
      );
    });

  return candidates[0] ?? null;
};

export const getCourseTermLabel = (course: ExternalCourse, selectedTerm: string | null | undefined): string | null => {
  if (selectedTerm !== undefined) {
    return selectedTerm;
  }

  return course.term?.label ?? null;
};

export const getCourseRowFromExternalCourse = (
  course: ExternalCourse,
  selectedTerm?: string | null
): CourseModernizerCourseRow => {
  const activeModernizedCourse = getActiveModernizedCourse(course);
  const termLabel = getCourseTermLabel(course, selectedTerm);
  const hasCompletedModernization = (course.modernizedCourses ?? []).some(
    (modernizedCourse) => modernizedCourse?.status === ModernizedAuditStatus.Modernized
  );

  if (!activeModernizedCourse) {
    return {
      course,
      termLabel,
      statusLabel: "Not Started",
      scoreLabel: "—",
      canEvaluate: true,
      canViewResults: false,
      canRerun: hasCompletedModernization,
      activeModernizedCourseId: null,
    };
  }

  if (activeModernizedCourse.status === ModernizedAuditStatus.NotStarted) {
    return {
      course,
      termLabel,
      statusLabel: "Not Started",
      scoreLabel: "—",
      canEvaluate: false,
      canViewResults: false,
      canRerun: hasCompletedModernization,
      activeModernizedCourseId: activeModernizedCourse.id,
    };
  }

  const canViewResults = Boolean(
    activeModernizedCourse.status && REVIEWABLE_MODERNIZATION_STATUSES.has(activeModernizedCourse.status)
  );
  const canRerun =
    (hasCompletedModernization && activeModernizedCourse.status !== ModernizedAuditStatus.InProgress) || canViewResults;

  return {
    course,
    termLabel,
    statusLabel: activeModernizedCourse.status === ModernizedAuditStatus.InProgress ? "In Progress" : "Modernized",
    scoreLabel: formatModernizedScore(activeModernizedCourse.totalScore, activeModernizedCourse.totalMaxScore),
    canEvaluate: false,
    canViewResults,
    canRerun,
    activeModernizedCourseId: activeModernizedCourse.id,
  };
};
